
import { Variant } from 'framer-motion';

// Enhanced animation variants optimized for mobile interactions
export const mobileAnimations = {
  // Fade in for text elements
  fadeIn: {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  },
  
  // Stagger children animations with improved performance
  staggerChildren: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  },
  
  // Optimized float animation
  float: {
    initial: { y: 0 },
    animate: { 
      y: [0, -8, 0],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        repeatType: "mirror" as const,
        ease: "easeInOut"
      }
    }
  },
  
  // Card animations with faster transitions for mobile
  cardHover: {
    rest: { scale: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
    hover: { scale: 1.02, y: -5, transition: { duration: 0.2, ease: "easeOut" } }
  },
  
  // Button animations optimized for touch
  buttonTap: {
    rest: { scale: 1 },
    tap: { scale: 0.97, transition: { duration: 0.1 } },
    hover: { scale: 1.02, transition: { duration: 0.2 } }
  },
  
  // Page transitions with hardware acceleration
  pageEntry: {
    initial: { opacity: 0, y: 15 },
    animate: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      } 
    },
    exit: { 
      opacity: 0, 
      y: -15, 
      transition: { 
        duration: 0.2,
        ease: "easeIn"
      } 
    }
  },
  
  // Mobile-optimized slide animations
  slideInUp: {
    hidden: { y: 15, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        damping: 25, 
        stiffness: 500 
      }
    }
  },
  
  slideInRight: {
    hidden: { x: 20, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        damping: 25, 
        stiffness: 400 
      }
    }
  },
  
  // Profile card animations
  profileCard: {
    hidden: { opacity: 0, scale: 0.98 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        type: "spring", 
        damping: 30, 
        stiffness: 400 
      }
    }
  },
  
  // Performance optimized animations for mobile
  optimizedFade: {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.25 }
    }
  },
  
  // Staggered list items with smoother animations
  listItem: {
    hidden: { opacity: 0, x: -5 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.25, ease: "easeOut" }
    }
  },
  
  // Pulse animation for notifications
  pulse: {
    initial: { scale: 1 },
    animate: { 
      scale: [1, 1.03, 1],
      transition: { 
        duration: 1.2, 
        repeat: Infinity,
        repeatType: "loop" as const,
        ease: "easeInOut"
      }
    }
  },
};

// Create animation variants with delayed start
export const createDelayedAnimation = (
  baseAnimation: { [key: string]: Variant },
  delay: number
) => {
  // Create a deep copy to avoid mutating the original
  const delayedAnimation = JSON.parse(JSON.stringify(baseAnimation));
  
  // Add delay to the visible variant transition
  if (delayedAnimation.visible && delayedAnimation.visible.transition) {
    delayedAnimation.visible.transition.delay = delay;
  } else if (delayedAnimation.visible) {
    delayedAnimation.visible.transition = { delay };
  }
  
  return delayedAnimation;
};

// New helper functions for combining animations
export const combineAnimations = (
  animations: { [key: string]: Variant }[]
): { [key: string]: Variant } => {
  const result: { [key: string]: Variant } = {};
  
  // Combine animations by merging their properties
  animations.forEach(animation => {
    Object.keys(animation).forEach(key => {
      if (!result[key]) {
        result[key] = { ...animation[key] };
      } else {
        result[key] = {
          ...result[key],
          ...animation[key]
        };
      }
    });
  });
  
  return result;
};

// Create staggered animations with custom delays
export const createStaggeredAnimations = (
  baseAnimation: { [key: string]: Variant },
  count: number,
  staggerDelay: number = 0.1
): { [key: string]: { [key: string]: Variant } } => {
  const result: { [key: string]: { [key: string]: Variant } } = {};
  
  for (let i = 0; i < count; i++) {
    result[`item-${i}`] = createDelayedAnimation(baseAnimation, i * staggerDelay);
  }
  
  return result;
};
